import React from 'react';
import {
  type FieldExtensionComponentProps,
  type FieldSchema,
  makeFieldSchema,
} from '@backstage/plugin-scaffolder-react';
import {
  createFormField,
  type FormField,
} from '@backstage/plugin-scaffolder-react/alpha';

import {
  ENTRA_GROUP_PICKER,
  ENTRA_SERVICE_PRINCIPAL_PICKER,
} from './field-names';
import { EntraGroupPickerComponent } from './group-picker';
import { EntraServicePrincipalPickerComponent } from './service-principal-picker';
import type { EntraGroup, EntraServicePrincipal } from './types';
import { groupValidator, servicePrincipalValidator } from './validation';

/**
 * Renders an Entra picker only for its object output schema.
 *
 * @remarks
 * A template can still name this field on another JSON Schema type. Delegating
 * that case to Backstage's normal `SchemaField`, after removing this field's
 * selector, preserves the framework fallback without recursively selecting the
 * same picker again.
 */
function objectPicker<TOutput>(
  Picker: (props: FieldExtensionComponentProps<TOutput>) => React.ReactElement,
): (props: FieldExtensionComponentProps<TOutput>) => React.ReactElement {
  return function ObjectPicker(props): React.ReactElement {
    if (props.schema.type === 'object')
      return React.createElement(Picker, props);

    const { ['ui:field']: _entraPickerField, ...uiSchema } = props.uiSchema;
    const SchemaField = props.registry.fields.SchemaField;
    if (SchemaField === undefined) {
      throw new Error(
        'Entra picker fallback needs the RJSF SchemaField in the registry',
      );
    }
    return React.createElement(SchemaField, { ...props, uiSchema });
  };
}

const EntraGroupObjectPicker = objectPicker(EntraGroupPickerComponent);
const EntraServicePrincipalObjectPicker = objectPicker(
  EntraServicePrincipalPickerComponent,
);

/** The two external template field names registered by this package. */
export const ENTRA_PICKER_FIELD_NAMES = [
  ENTRA_GROUP_PICKER,
  ENTRA_SERVICE_PRINCIPAL_PICKER,
] as const;

/** Closed group picker schema that accepts no author supplied `ui:options`. */
export const entraIdGroupPickerSchema: FieldSchema<EntraGroup, undefined> =
  makeFieldSchema({
    output: z => z.custom<EntraGroup>(),
    uiOptions: z => z.never().optional(),
  });

/** Closed service-principal schema that accepts no author supplied `ui:options`. */
export const entraIdServicePrincipalPickerSchema: FieldSchema<
  EntraServicePrincipal,
  undefined
> = makeFieldSchema({
  output: z => z.custom<EntraServicePrincipal>(),
  uiOptions: z => z.never().optional(),
});

type RegisteredFormField = FormField & {
  readonly component: unknown;
  readonly name: string;
  readonly schema: unknown;
  readonly validation: unknown;
};
function isRegisteredFormField(field: FormField): field is RegisteredFormField {
  return (
    'name' in field &&
    typeof field.name === 'string' &&
    'component' in field &&
    'schema' in field &&
    'validation' in field
  );
}

/** Reads the Backstage registration details needed by contract tests. */
export function registeredFieldOf(field: FormField): RegisteredFormField {
  if (!isRegisteredFormField(field)) {
    throw new TypeError(
      'A form field lacks the registration details createFormField stores.',
    );
  }
  return field;
}

/** Form field registration for the Microsoft Entra group output. */
export const EntraIdGroupPicker: FormField = createFormField({
  component: EntraGroupObjectPicker,
  name: ENTRA_GROUP_PICKER,
  schema: entraIdGroupPickerSchema,
  validation: groupValidator,
});

/** Form field registration for the Microsoft Entra service-principal output. */
export const EntraIdServicePrincipalPicker: FormField = createFormField({
  component: EntraServicePrincipalObjectPicker,
  name: ENTRA_SERVICE_PRINCIPAL_PICKER,
  schema: entraIdServicePrincipalPickerSchema,
  validation: servicePrincipalValidator,
});
